import { useEffect, useRef, useState, useCallback } from 'react'

type Mutex = {
  holder: string | null
  acquire: () => boolean
  release: () => void
}

export function usePttMutex(channelName?: string): Mutex {
  const tabIdRef = useRef<string>('')
  if (!tabIdRef.current) {
    const existing =
      typeof window !== 'undefined' &&
      window.name &&
      window.name.startsWith('tab-')
        ? window.name
        : ''
    const id = existing || `tab-${Math.random().toString(36).slice(2)}`
    if (!existing) window.name = id
    tabIdRef.current = id
  }

  const bcRef = useRef<BroadcastChannel | null>(null)
  const [holder, setHolder] = useState<string | null>(null)

  const acquire = useCallback(() => {
    if (holder && holder !== tabIdRef.current) return false
    if (typeof window !== 'undefined' && bcRef.current) {
      bcRef.current.postMessage({ type: 'LOCK', holder: tabIdRef.current })
    } else {
      setHolder(tabIdRef.current)
    }
    return true
  }, [holder])

  const release = useCallback(() => {
    if (typeof window !== 'undefined' && bcRef.current) {
      bcRef.current.postMessage({ type: 'UNLOCK' })
    } else {
      setHolder(null)
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      bcRef.current = new BroadcastChannel(`zello-ptt:${channelName || 'default'}`)
      bcRef.current.onmessage = (e: MessageEvent) => {
        if (e.data?.type === 'LOCK') setHolder(e.data.holder || null)
        if (e.data?.type === 'UNLOCK') setHolder(null)
      }
    }
    return () => {
      try { bcRef.current?.close() } catch { /* no-op */ }
    }
  }, [channelName])

  useEffect(() => {
    const end = () => release()
    window.addEventListener('blur', end)
    document.addEventListener('visibilitychange', end)
    return () => {
      window.removeEventListener('blur', end)
      document.removeEventListener('visibilitychange', end)
    }
  }, [release])

  return { holder, acquire, release }
}
